import React, { useEffect, useRef, useMemo } from 'react';
import { type MutableState } from 'final-form';
import { Form as ReactFinalForm } from 'react-final-form';
import { identity } from 'ramda';

import * as hooks from 'js/components/common/Form/hooks';
import * as Types from 'js/components/common/Form/types';

/* core, added mutators */
export const Form = <FormValues, >({
    onSubmit,
    validate,
    formatData,
    validationConfig,
    fieldsToReplaceComma,
    skipValidationFields, // disabled or readOnly fields should be passed here to avoid its validation on submit
    validateOnBlur,

    /* buzz props */
    mutators: mutatorsCustom,
    destroyedFn = identity,

    ...props
}: Types.IExtendedFormProps<FormValues>): React.ReactElement => {
    const validateWrapped = hooks.useValidateWrapped({ validate, validationConfig, skipValidationFields });
    const onSubmitWrapped = hooks.useOnSubmitWrapped({ onSubmit, formatData, fieldsToReplaceComma });

    /* buzz start */
    const lastFieldName = useRef('');

    const mutators = useMemo(() => ({
        buttonBlur: (args: any, state: MutableState<FormValues>): void => {
            state.fields[args[0]].blur();
        },
        focusField: (args: any, { lastFormState }: MutableState<FormValues>): void => {
            lastFieldName.current = typeof args?.[0] === 'string' ? args?.[0] : lastFormState?.active as string || '';
        },
        getLastFieldName: () => lastFieldName.current || 'unknown',
        ...mutatorsCustom || {}
    }), [mutatorsCustom]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => () => destroyedFn({ lastFieldName: lastFieldName.current }), []);
    /* buzz end */

    return (
        <ReactFinalForm<FormValues>
            onSubmit={onSubmitWrapped}
            validate={validateWrapped}
            validateOnBlur={validateOnBlur}
            mutators={mutators}
            {...props}
        />
    );
};

// todo Remove this
export default Form;
